import { FunctionComponent, SVGProps, useCallback, useState } from 'react';
import { SelectProps } from 'antd';
import { StyledXIcon } from 'components/atoms/StyledIcons';
import { AppText } from 'components/atoms/CommonAppComponents/AppText/AppText';
import {
    GlobalStyle,
    InnerContainer,
    OuterContainer,
    StyledAppBaseSelect,
} from './AppBaseSelect.styled';
import { SelectTagRender } from '../components/SelectTagRender/SelectTagRender';
import { SelectSuffixIcon } from '../components/SelectSuffixIcon/SelectSuffixIcon';

export type AppBaseSelectProps = SelectProps & {
    label?: string;
    isFitContentWidth?: boolean;
    Icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
    accessibilityLabel?: string;
};

export const AppBaseSelect = ({
    label,
    Icon,
    loading,
    isFitContentWidth,
    optionFilterProp = 'label',
    mode,
    suffixIcon,
    accessibilityLabel,
    allowClear,
    ...props
}: AppBaseSelectProps) => {
    const [dropdown, setDropdown] = useState(false);
    const isClearAllowed = allowClear !== false;
    let allowClearConfig: AppBaseSelectProps['allowClear'] = { clearIcon: <StyledXIcon /> };

    if (allowClear === false) {
        allowClearConfig = false;
    } else if (allowClear && typeof allowClear === 'object') {
        allowClearConfig = allowClear;
    }

    const handleClick = useCallback((open: boolean) => {
        setDropdown(open);
    }, []);

    return (
        <OuterContainer isFitContentWidth={isFitContentWidth}>
            {label ? (
                <AppText margin={0} textType="BodyMSSemiBold">
                    {label}
                </AppText>
            ) : null}
            <InnerContainer>
                <GlobalStyle />
                {Icon && <Icon width={20} height={20} />}
                <StyledAppBaseSelect
                    {...props}
                    tagRender={SelectTagRender}
                    maxTagCount="responsive"
                    optionFilterProp={optionFilterProp}
                    suffixIcon={<SelectSuffixIcon loading={loading} suffixIcon={suffixIcon} />}
                    allowClear={allowClearConfig}
                    loading={loading}
                    isFitContentWidth={isFitContentWidth}
                    mode={mode}
                    aria-label={accessibilityLabel}
                    showSearch
                    filterOption={(input, option) =>
                        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                    open={dropdown}
                    onDropdownVisibleChange={handleClick}
                    onKeyDown={(e) => {
                        if (isClearAllowed && (e.key === 'Delete' || e.key === 'Backspace')) {
                            e.preventDefault();

                            if (
                                mode === 'multiple' &&
                                props.value &&
                                Array.isArray(props.value) &&
                                props.value.length > 0
                            ) {
                                const newValue = [...props.value];
                                newValue.pop();

                                if (props.onChange) {
                                    props.onChange(newValue, {} as never);
                                }
                            } else if (
                                mode !== 'multiple' &&
                                props.value !== undefined &&
                                props.value !== null
                            ) {
                                if (props.onChange) {
                                    props.onChange(null, {} as never);
                                }
                            }
                        }
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                    }}
                />
            </InnerContainer>
        </OuterContainer>
    );
};
